import { makeXCSSValidator } from './xcssValidate';
import { type BorderRadius } from '@atlaskit/primitives';

const borderRadiusTokens: BorderRadius[] = [
	'radius.xsmall',
	'radius.small',
	'radius.medium',
	'radius.large',
	'radius.xlarge',
	'radius.full',
	'radius.tile',
];

// `border.radius` is a legacy token that is used in Forge UI. The token was initially supported by ADS but has since been removed.
// Forge needs to continue support this token for backwards compatibility.
const borderRadiusSupportedValues = [...borderRadiusTokens, 'border.radius'];

export const xcssValidator = makeXCSSValidator({
	// text related props
	textAlign: {
		allowCSS: true,
	},

	// color related props
	color: true,
	boxShadow: true,
	opacity: true,
	backgroundColor: true,
	borderColor: true,
	borderBlockColor: true,
	borderBlockEndColor: true,
	borderBlockStartColor: true,
	borderBottomColor: true,
	borderInlineColor: true,
	borderInlineEndColor: true,
	borderInlineStartColor: true,
	borderLeftColor: true,
	borderRightColor: true,
	borderTopColor: true,

	overflow: {
		supportedValues: ['hidden', 'visible', 'scroll', 'auto'],
	},

	overflowX: {
		supportedValues: ['hidden', 'visible', 'scroll', 'auto'],
	},
	overflowY: {
		supportedValues: ['hidden', 'visible', 'scroll', 'auto'],
	},

	// layout and space related props
	display: {
		supportedValues: ['block', 'inline-block', 'inline', 'none'],
	},
	flexGrow: {
		allowCSS: true,
	},
	width: {
		allowCSS: true,
	},
	height: {
		allowCSS: true,
	},
	minWidth: {
		allowCSS: true,
	},
	maxWidth: {
		allowCSS: true,
	},
	minHeight: {
		allowCSS: true,
	},
	maxHeight: {
		allowCSS: true,
	},
	margin: true,
	marginBlock: true,
	marginBlockEnd: true,
	marginBlockStart: true,
	marginBottom: true,
	marginInline: true,
	marginInlineEnd: true,
	marginInlineStart: true,
	marginLeft: true,
	marginRight: true,
	marginTop: true,
	padding: true,
	paddingBlock: true,
	paddingBlockEnd: true,
	paddingBlockStart: true,
	paddingBottom: true,
	paddingInline: true,
	paddingInlineEnd: true,
	paddingInlineStart: true,
	paddingLeft: true,
	paddingRight: true,
	paddingTop: true,

	// other box related props
	borderRadius: { supportedValues: borderRadiusSupportedValues },
	borderBottomLeftRadius: { supportedValues: borderRadiusSupportedValues },
	borderBottomRightRadius: { supportedValues: borderRadiusSupportedValues },
	borderTopLeftRadius: { supportedValues: borderRadiusSupportedValues },
	borderTopRightRadius: { supportedValues: borderRadiusSupportedValues },
	borderEndEndRadius: { supportedValues: borderRadiusSupportedValues },
	borderEndStartRadius: { supportedValues: borderRadiusSupportedValues },
	borderStartEndRadius: { supportedValues: borderRadiusSupportedValues },
	borderStartStartRadius: { supportedValues: borderRadiusSupportedValues },
	borderWidth: true,
	borderBlockWidth: true,
	borderBlockEndWidth: true,
	borderBlockStartWidth: true,
	borderBottomWidth: true,
	borderInlineWidth: true,
	borderInlineEndWidth: true,
	borderInlineStartWidth: true,
	borderLeftWidth: true,
	borderRightWidth: true,
	borderTopWidth: true,

	// other props not in tokens based props
	borderTopStyle: {
		supportedValues: ['dotted', 'dashed', 'solid', 'none', 'hidden'],
	},
	borderBottomStyle: {
		supportedValues: ['dotted', 'dashed', 'solid', 'none', 'hidden'],
	},
	borderRightStyle: {
		supportedValues: ['dotted', 'dashed', 'solid', 'none', 'hidden'],
	},
	borderLeftStyle: {
		supportedValues: ['dotted', 'dashed', 'solid', 'none', 'hidden'],
	},
	borderStyle: {
		supportedValues: ['dotted', 'dashed', 'solid', 'none', 'hidden'],
	},
	position: {
		supportedValues: ['relative', 'static'],
	},
});

export type XCSSProp = ReturnType<typeof xcssValidator>;
